import { useParams, Link, Navigate } from "react-router-dom";
import { PublicLayout } from "@/components/PublicLayout";
import { Crumbs, ArticleCard, ContentCta, Rich } from "@/components/content/ContentBits";
import { useContent, imageFor, fmtDate } from "@/content/store";

const slugId = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const Block = ({ b, i }) => {
  if (b.type === "h3") return <h3 className="mt-8 font-display text-xl text-foreground">{b.text}</h3>;
  if (b.type === "ul" || b.type === "ol") {
    const L = b.type;
    return <L className={`mt-4 space-y-2 pl-5 ${L === "ul" ? "list-disc" : "list-decimal"} marker:text-[#E9C98B]/70`}>{b.items.map((x) => <li key={x}><Rich text={x} /></li>)}</L>;
  }
  if (b.type === "tagset") return (
    <div data-testid={`tagset-${i}`} className="mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
      <p className="text-sm font-semibold text-[#F3DDAE]">{b.name}</p>
      <ul className="mt-3 flex flex-wrap gap-2" aria-label={`${b.name}: 13 tags`}>
        {b.tags.map((t) => <li key={t} className="rounded-full border border-[#5EE6D0]/25 bg-[#5EE6D0]/[0.06] px-3 py-1 font-mono text-[13px] text-foreground">{t}</li>)}
      </ul>
      <p className="mt-3 text-xs text-muted-foreground">13 tags · each 20 characters or fewer</p>
    </div>
  );
  if (b.type === "titles") return (
    <ol className="mt-4 space-y-3">
      {b.items.map((t) => <li key={t} className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 font-mono text-[13px] leading-relaxed text-foreground">{t} <span className="text-muted-foreground">({t.length} chars)</span></li>)}
    </ol>
  );
  if (b.type === "note") return <p className="mt-5 rounded-xl border-l-2 border-[#E9C98B]/60 bg-white/[0.03] px-4 py-3 text-sm"><Rich text={b.text} /></p>;
  return <p className="mt-4"><Rich text={b.text} /></p>;
};

const NotFound = ({ scheduled }) => (
  <PublicLayout>
    <div data-testid="content-not-found" className="mx-auto max-w-3xl px-5 py-24 sm:px-8">
      <h1 className="font-display text-4xl">{scheduled ? "This page isn't published yet." : "Page not found."}</h1>
      <p className="mt-4 text-muted-foreground">{scheduled ? "It's scheduled for later. Browse what's live now." : "This page doesn't exist. Browse the guides and tag pages instead."}</p>
      <div className="mt-6 flex gap-3"><Link to="/blog" className="text-[#5EE6D0] underline">Guides</Link><Link to="/etsy-tags" className="text-[#5EE6D0] underline">Etsy Tags</Link></div>
    </div>
  </PublicLayout>
);

export default function ArticlePage({ kind }) {
  const { slug } = useParams();
  const a = useContent(`/content/${kind}/${slug}`, true);
  if (a?.redirect_to) return <Navigate to={a.redirect_to} replace />;
  if (a?.loadError) return (
    <PublicLayout>
      <div data-testid="content-load-error" className="mx-auto max-w-3xl px-5 py-24 text-center sm:px-8">
        <p className="text-lg text-foreground">This page didn't load. Check your connection and try again.</p>
        <button type="button" data-testid="content-load-retry" onClick={() => window.location.reload()}
          className="mt-6 inline-flex min-h-[44px] items-center rounded-full border border-white/15 px-6 text-sm hover:border-[#E9C98B]/40">Try again</button>
      </div>
    </PublicLayout>
  );
  if (a?.notFound) return <NotFound scheduled={a.scheduled} />;
  if (!a) return <PublicLayout><div className="mx-auto h-[60vh] max-w-3xl px-5"><div className="h-64 rounded-2xl shimmer" /></div></PublicLayout>;
  const hub = kind === "blog" ? ["Guides", "/blog"] : ["Etsy Tags", "/etsy-tags"];
  const toc = a.type === "guide" && a.sections.length > 3;
  return (
    <PublicLayout>
      <article data-testid="content-article" className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14">
        <Crumbs testid="content-breadcrumbs" items={[["Home", "/"], hub, [a.type === "guide" ? a.h1 : a.product]]} />
        <h1 data-testid="content-h1" className="font-display text-4xl leading-[1.08] sm:text-5xl">{a.h1}</h1>
        <p data-testid="content-byline" className="mt-5 text-sm text-muted-foreground">
          By <Link to="/about" className="text-foreground underline-offset-4 hover:underline">{a.author}</Link> · Published {fmtDate(a.published_at)} · Updated {fmtDate(a.updated_at)}
        </p>
        <div className="mt-8 text-[17px] leading-[1.75] text-muted-foreground [&_strong]:text-foreground">
          <p data-testid="content-intro" className="text-lg text-foreground/90"><Rich text={a.intro} /></p>
          <img src={imageFor(a.category)} alt={a.image_alt} width="1200" height="805" loading="lazy" decoding="async"
            className="mt-8 aspect-[3/2] w-full rounded-2xl border border-white/10 object-cover" data-testid="content-hero-image" />
          {toc && (
            <nav aria-label="On this page" data-testid="content-toc" className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm">
              <p className="font-semibold text-foreground">On this page</p>
              <ol className="mt-3 space-y-1.5">{a.sections.map((s) => <li key={s.h2}><a href={`#${slugId(s.h2)}`} className="hover:text-foreground">{s.h2}</a></li>)}</ol>
            </nav>
          )}
          {a.sections.map((s, si) => (
            <section key={s.h2}>
              <h2 id={slugId(s.h2)} className="mt-12 scroll-mt-24 font-display text-2xl leading-snug text-foreground sm:text-3xl">{s.h2}</h2>
              {s.blocks.map((b, bi) => <Block key={bi} b={b} i={`${si}-${bi}`} />)}
              {si === 1 && <ContentCta />}
            </section>
          ))}
          <section data-testid="content-faq">
            <h2 className="mt-14 font-display text-2xl text-foreground sm:text-3xl">FAQ</h2>
            {a.faq.map((f) => (
              <div key={f.q} className="mt-6"><h3 className="font-semibold text-foreground">{f.q}</h3><p className="mt-2"><Rich text={f.a} /></p></div>
            ))}
          </section>
          <section data-testid="content-sources" className="mt-12 border-t border-white/10 pt-6 text-sm">
            <h2 className="font-semibold text-foreground">Sources</h2>
            <ul className="mt-2 space-y-1">{a.sources.map((s) => <li key={s.url}><a href={s.url} rel="nofollow noopener" target="_blank" className="underline underline-offset-4 hover:text-foreground">{s.label}</a></li>)}</ul>
            <p className="mt-4 text-xs">Etsy rules change. Check the Etsy Help Center for the current limits. Suggestions here are starting points; validate them against your own research and Etsy Stats.</p>
          </section>
        </div>
        <ContentCta />
        {a.related?.length > 0 && (
          <section data-testid="content-related" className="mt-6">
            <h2 className="font-display text-2xl">Related</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">{a.related.map((r) => <ArticleCard key={r.slug} a={r} testid={`related-${r.slug}`} />)}</div>
          </section>
        )}
      </article>
    </PublicLayout>
  );
}
